/**
 * useLogout Hook
 *
 * Provides logout functionality that works in any context (dashboard or account activation)
 * This hook can be used independently without calling useAuthentication
 */

import { useDispatch } from "react-redux";
import { useAuth } from "react-oidc-context";
import { setGlobalLoading, setGlobalUserInfo, setToken } from "scenes/redux";
import { UserInfo } from "types/UserInfo";

export const useLogout = () => {
  const auth = useAuth();
  const dispatch = useDispatch();

  const emptyUserInfo: UserInfo = {
    sub: "",
    cidpId: "",
    email: "",
    ext: {
      sub_type: "",
      auth_provider: "",
    },
    given_name: "",
    family_name: "",
    preferred_username: "",
  };

  const logout = async () => {
    try {
      dispatch(setToken(""));
      dispatch(setGlobalUserInfo(emptyUserInfo));
      dispatch(setGlobalLoading(false));

      // Set a timestamp in localStorage to trigger the storage event in other tabs
      localStorage.setItem("logout-event", Date.now().toString());

      // Only attempt token revocation if auth context is available
      if (auth?.user) {
        await auth.revokeTokens();
        await auth.signoutRedirect({
          post_logout_redirect_uri: `${window.location.origin}/signedOut`,
          id_token_hint: auth?.user?.id_token,
        });
      } else {
        // If no auth context, just redirect to signout page
        window.location.href = `${window.location.origin}/signedOut`;
      }
    } catch (error) {
      // Fallback redirect even if logout fails
      window.location.href = `${window.location.origin}/signedOut`;
    }
  };

  return logout;
};

export default useLogout;
